// Lightweight localStorage snapshot of the most-used SPA data so the
// invoices and contacts screens can render instantly on boot, before the
// /invoices and /contacts network requests resolve. Sync API (localStorage)
// so the snapshot is available in the same tick as init() runs.
//
// Scoped by user id — switching accounts in the same browser doesn't leak.
// Bounded so we never blow past localStorage's per-origin quota: most-recent
// 200 invoices, all contacts (they're small), summary fields only.

const KEY = "sendinvo.cache.v1";
const MAX_INVOICES = 200;

function read() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function write(snapshot) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(snapshot));
  } catch {
    // Quota exceeded or storage unavailable — drop silently. The cache is
    // a perf optimisation, not the source of truth.
  }
}

// Every list screen paints twice: once from the cached data, then again once
// the network revalidate lands. When the server returns exactly what was
// already on screen — the common case, and always the case when a drawer is
// opened over a list that was fresh a moment ago — the second paint rewrites
// identical markup, and rebuilding the DOM destroys and recreates every
// <img> icon in it. The browser re-decodes each one, which reads as the
// icons flashing, and the drawer sliding in on top makes it worse.
//
// Fingerprint what the paint reads from, and skip the repaint when nothing
// moved. Skipping is safe: the DOM is untouched, so its handlers stay bound.
export function listFingerprint(...lists) {
  try {
    return JSON.stringify(lists);
  } catch {
    // Circular or otherwise unserialisable — treat as always-changed rather
    // than risk holding a stale list on screen.
    return null;
  }
}

export function clearCache() {
  try { window.localStorage.removeItem(KEY); } catch {}
}

// Hydrate `state.invoices` and `state.contacts` from the snapshot if the
// cached userId matches the current session. Returns true if anything was
// hydrated, so the caller can choose to render immediately.
export function hydrateFromCache(state, userId) {
  if (!userId) return false;
  const snap = read();
  if (!snap || snap.userId !== String(userId)) return false;
  let hydrated = false;
  if (Array.isArray(snap.invoices)) {
    state.invoices = snap.invoices;
    state._loaded.invoices = true;
    hydrated = true;
  }
  if (Array.isArray(snap.contacts)) {
    state.contacts = snap.contacts;
    state._loaded.contacts = true;
    hydrated = true;
  }
  if (Array.isArray(snap.quotes)) {
    state.quotes = snap.quotes;
    state._loaded.quotes = true;
    hydrated = true;
  }
  return hydrated;
}

export function persistSnapshot(state) {
  if (!state.user?.id) return;
  const invoices = Array.isArray(state.invoices)
    ? state.invoices.slice(0, MAX_INVOICES)
    : [];
  const contacts = Array.isArray(state.contacts) ? state.contacts : [];
  const quotes = Array.isArray(state.quotes)
    ? state.quotes.slice(0, MAX_INVOICES)
    : [];
  write({
    userId: String(state.user.id),
    invoices,
    contacts,
    quotes,
    savedAt: Date.now()
  });
}
